import { afterNextRender, AfterViewInit, Component, computed, effect, HostListener, inject, Injector, OnDestroy, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Clipboard } from '@angular/cdk/clipboard';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import {
  CellKeyDownEvent, ColDef, FullWidthCellKeyDownEvent, GetContextMenuItemsParams, GetDetailRowDataParams, GetRowIdParams, GridOptions,
  ICellRendererParams, IDetailCellRendererParams, IRowNode, RowClickedEvent, StatusPanelDef, ValueFormatterParams
} from 'ag-grid-community';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { PayerEditStore } from '../../../store/payer-edit.store';
import { PayerConfigurationStore, serviceYearComparator, textComparator } from '../../../store/payer-configuration.store';
import { PayerConfigQuery } from '../../../models/payer-config-query';
import { PayerConfigFilterValues } from '../../../models/payer-config-filter-values';
import { PayerConfigItem } from '../../../models/payer-config-item';
import { PayerConfigGroup } from '../../../models/payer-config-group';
import { StringFormatter } from '../../../helpers/string-formatter';
import { PayerPolicyFunctions } from '../../../helpers/payer-policy-functions';
import { PayerFilterSelectComponent } from '../../field-components/payer-filter-select/payer-filter-select.component';
import { FilterMultiSelectComponent, FilterMultiSelectOption } from '../../field-components/filter-multi-select/filter-multi-select.component';
import { YearRangeFilterComponent } from '../../field-components/year-range-filter/year-range-filter.component';
import { AgPaginatorComponent, IAgPaginator } from '../../ag-grid-components/ag-paginator/ag-paginator.component';
import { AgExportButtonComponent, IAgExportButton } from '../../ag-grid-components/ag-export-button/ag-export-button.component';
import { AgNoRowsOverlayComponent } from '../../ag-grid-components/ag-no-rows-overlay/ag-no-rows-overlay.component';
import { AgLoadingOverlayComponent } from '../../ag-grid-components/ag-loading-overlay/ag-loading-overlay.component';
import { AgYesNoPillRendererComponent } from '../../ag-grid-components/ag-yes-no-pill-renderer/ag-yes-no-pill-renderer.component';
import { AgTwoLineCellRendererComponent, IAgTwoLineCellRendererParams } from '../../ag-grid-components/ag-two-line-cell-renderer/ag-two-line-cell-renderer.component';
import { AgPayerCellRendererComponent } from '../../ag-grid-components/ag-payer-cell-renderer/ag-payer-cell-renderer.component';
import { AgRowActionsRendererComponent, IAgRowActionsRendererParams } from '../../ag-grid-components/ag-row-actions-renderer/ag-row-actions-renderer.component';
import { AddUpdatePayerDialogComponent, AddUpdatePayerDialogModel } from '../../../dialogs/add-update-payer-dialog/add-update-payer-dialog.component';
import { AddUpdateRiskPolicyDialogComponent, AddUpdateRiskPolicyDialogModel } from '../../../dialogs/add-update-risk-policy-dialog/add-update-risk-policy-dialog.component';

type PolicyDialogMode = 'edit' | 'copy';

// Payer Configuration (VC-3094): one row per payer, expanding to a nested grid of its service year
// risk policies. List state and service calls live in PayerConfigurationStore; the filters still run
// server-side and reload the list as they change.
@Component({
    selector: 'app-payer-configuration',
    templateUrl: './payer-configuration.component.html',
    styleUrls: ['./payer-configuration.component.scss'],
    providers: [PayerConfigurationStore],
    imports: [
      MatButton, MatIcon, MatTooltip,
      PayerFilterSelectComponent, FilterMultiSelectComponent, YearRangeFilterComponent,
      AgGridModule
    ]
})
export class PayerConfigurationComponent implements AfterViewInit, OnDestroy {

  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly clipboard = inject(Clipboard);
  readonly store = inject(PayerConfigurationStore);
  // The payer being edited; the payer and risk policy dialogs read it from here.
  private readonly payerEditStore = inject(PayerEditStore);
  private readonly injector = inject(Injector);

  isPayerReady = false;
  readonly isGridReady = signal(false);

  // Status, Risk Adj. Policy, Omit Payer and Preferred Action: always at least one option checked;
  // every option checked means no filter. Status values are the payer's Disabled flag.
  readonly statusOptions: FilterMultiSelectOption<boolean>[] = [{ value: false, label: 'Active' }, { value: true, label: 'Inactive' }];
  readonly yesNoOptions: FilterMultiSelectOption<boolean>[] = [{ value: true, label: 'Yes' }, { value: false, label: 'No' }];
  readonly preferredActionOptions = computed<FilterMultiSelectOption<number>[]>(() =>
    (this.store.preferredActionOptions() ?? []).map(o => ({ value: o.ID, label: o.Description })));

  // Set once every filter has loaded and the first query has run; filter changes before then are ignored.
  private filtersReady = false;
  private readonly filterChanged$ = new Subject<void>();
  // Payer to expand once the next load renders (after a policy save).
  private expandPayerId: number | null = null;
  private pageSizeApplied = false;

  readonly subtitle = computed(() => {
    const payers = this.store.payerCount();
    const policies = this.store.policyCount();
    return `${payers} ${payers === 1 ? 'Payer' : 'Payers'} · ${policies} Service Year ${policies === 1 ? 'Policy' : 'Policies'}`;
  });

  form: FormGroup = this.fb.group({});

  subs: Subscription[] = [];

  gridContext = { componentParent: this };

  statusBar: { statusPanels: StatusPanelDef[] } = {
    statusPanels: [
      { statusPanel: AgPaginatorComponent, key: 'ag-paginator' },
      { statusPanel: AgExportButtonComponent, key: 'export-btn' },
    ],
  };

  noRowsOverlayComponent: any = AgNoRowsOverlayComponent;
  noRowsOverlayComponentParams: any = {
    noRowsMessageFunc: () => 'No payers match the current filters',
  };

  loadingOverlayComponent: any = AgLoadingOverlayComponent;

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    suppressHeaderMenuButton: true,
    menuTabs: [],
  };

  // ── Main grid: one row per payer ─────────────────────────────────────────
  columnDefs: ColDef<PayerConfigGroup>[] = [
    {
      field: 'PayerName', headerName: 'Payer', flex: 3, minWidth: 300, sort: 'asc',
      comparator: textComparator,
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: { innerRenderer: AgPayerCellRendererComponent },
    },
    {
      field: 'Abbreviation', headerName: 'Abbreviation', flex: 1, minWidth: 120,
      comparator: textComparator,
      cellRenderer: (params: ICellRendererParams) => this.codeChip(params.value, 'ag-abbr-chip'),
    },
    {
      colId: 'ServiceYears', headerName: 'Service Years', flex: 1.4, minWidth: 160,
      valueGetter: params => this.serviceYearRange(params.data),
      cellRenderer: AgTwoLineCellRendererComponent,
      cellRendererParams: {
        secondary: (params: ICellRendererParams<PayerConfigGroup>) => {
          const count = params.data?.Rows.length ?? 0;
          return `${count} ${count === 1 ? 'Policy' : 'Policies'}`;
        }
      } as IAgTwoLineCellRendererParams,
    },
    {
      field: 'Status', headerName: 'Status', flex: 1, minWidth: 110,
      cellDataType: false,
      valueFormatter: this.statusValue,
      cellRenderer: AgYesNoPillRendererComponent,
    },
    {
      colId: 'actions', headerName: 'Action', width: 84, minWidth: 84, maxWidth: 84,
      sortable: false, resizable: false, suppressMovable: true,
      headerClass: 'payer-configuration__center-header',
      cellRenderer: AgRowActionsRendererComponent,
      cellRendererParams: {
        actions: [{ icon: 'edit', label: 'Edit Payer', onClick: (group: PayerConfigGroup) => this.onEditPayerClick(group) }]
      } as IAgRowActionsRendererParams<PayerConfigGroup>,
    },
  ];

  // ── Nested grid: one row per service year policy (config-list rows) ──────
  private detailColumnDefs: ColDef<PayerConfigItem>[] = [
    {
      field: 'RiskAdjustmentPolicy.ServiceYearStart', headerName: 'Service Year Start', flex: 1.1, minWidth: 130,
      sort: 'desc', comparator: serviceYearComparator,
    },
    {
      field: 'RiskAdjustmentPolicy.ServiceYearEnd', headerName: 'Service Year End', flex: 1.1, minWidth: 130,
      comparator: serviceYearComparator,
    },
    {
      field: 'RiskAdjustmentPolicy.HasRiskAdjustment', headerName: 'Risk Adj. Policy', flex: 1, minWidth: 110,
      cellDataType: false,
      valueFormatter: this.booleanValue,
      cellRenderer: AgYesNoPillRendererComponent,
    },
    {
      field: 'RiskAdjustmentPolicy.HasManualOmit', headerName: 'Omit Payer', flex: 0.9, minWidth: 100,
      cellDataType: false,
      valueFormatter: this.manualOmitValue,
      cellRenderer: AgYesNoPillRendererComponent,
    },
    {
      field: 'RiskAdjustmentPolicy.PreferredActionSubmissionType', headerName: 'Preferred Action', flex: 1, minWidth: 120,
      valueFormatter: this.paFilterOptionValue,
      comparator: (a, b) => textComparator(a?.Description, b?.Description),
      cellRenderer: (params: ICellRendererParams) => this.codeChip(params.valueFormatted),
    },
    {
      field: 'ReceivingMAO', headerName: 'Receiving MAO-004', flex: 1.1, minWidth: 120,
      cellDataType: false,
      valueFormatter: this.booleanValue,
      cellRenderer: AgYesNoPillRendererComponent,
    },
    {
      colId: 'TemplateCount', headerName: 'Templates', type: 'rightAligned', flex: 0.8, minWidth: 95,
      valueGetter: params => PayerPolicyFunctions.templateCount(params.data?.RiskAdjustmentPolicy),
      tooltip: params => PayerPolicyFunctions.templateNames(params.data?.RiskAdjustmentPolicy) || null,
    },
    {
      colId: 'actions', headerName: 'Action', width: 96, minWidth: 96, maxWidth: 96,
      sortable: false, resizable: false, suppressMovable: true,
      headerClass: 'payer-configuration__center-header',
      cellRenderer: AgRowActionsRendererComponent,
      cellRendererParams: {
        actions: [
          { icon: 'edit', label: 'Edit Policy', onClick: (row: PayerConfigItem) => this.onPolicyDialogClick('edit', row) },
          { icon: 'content_copy', label: 'Copy Policy', onClick: (row: PayerConfigItem) => this.onPolicyDialogClick('copy', row) },
        ]
      } as IAgRowActionsRendererParams<PayerConfigItem>,
    },
  ];

  gridOptions: GridOptions<PayerConfigGroup> = {
    masterDetail: true,
    isRowMaster: group => group.Rows.length > 0,
    detailRowAutoHeight: true,
    detailCellRendererParams: {
      // Keeps an open nested grid in sync when its payer's rows reload.
      refreshStrategy: 'rows',
      detailGridOptions: {
        context: this.gridContext,
        columnDefs: this.detailColumnDefs,
        defaultColDef: { sortable: true, resizable: true, suppressHeaderMenuButton: true, menuTabs: [] },
        getRowId: (params: GetRowIdParams<PayerConfigItem>) => `${params.data.RiskAdjustmentPolicy.PolicyID}`,
        getContextMenuItems: params => this.getContextMenuItems(params),
        onCellKeyDown: event => this.onCellKeyDown(event),
        tooltipShowDelay: 0,
        rowHeight: 40,
        headerHeight: 36,
        domLayout: 'autoHeight',
      } as GridOptions<PayerConfigItem>,
      getDetailRowData: (params: GetDetailRowDataParams<PayerConfigGroup>) => params.successCallback(params.data.Rows),
    } as Partial<IDetailCellRendererParams<PayerConfigGroup, PayerConfigItem>>,
    rowHeight: 52,
    tooltipShowDelay: 0,
  };

  getRowId = (params: GetRowIdParams<PayerConfigGroup>) => `${params.data.PayerID}`;

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  @ViewChild(PayerFilterSelectComponent) payerComp: PayerFilterSelectComponent;
  @ViewChild('configStatus') statusComp: FilterMultiSelectComponent<boolean>;
  @ViewChild('configRiskPolicy') riskPolicyComp: FilterMultiSelectComponent<boolean>;
  @ViewChild('configOmitPayer') omitPayerComp: FilterMultiSelectComponent<boolean>;
  @ViewChild('configPreferredAction') paSelectComp: FilterMultiSelectComponent<number>;
  @ViewChild(YearRangeFilterComponent) serviceYearsComp: YearRangeFilterComponent;

  @HostListener('window:resize')
  onResize() {
    this.agGrid?.api?.sizeColumnsToFit();
  }

  constructor() {
    this.subs.push(this.filterChanged$.pipe(debounceTime(300)).subscribe(() => this.loadData()));

    // Apply the saved page size once both it and the grid are ready.
    effect(() => {
      const pageSize = this.store.pageSize();
      if (this.pageSizeApplied || pageSize <= 0 || !this.isGridReady() || this.agGrid?.api?.isDestroyed()) {
        return;
      }

      this.pageSizeApplied = true;
      this.agGrid?.api?.getStatusPanel<IAgPaginator>('ag-paginator')?.setPageSize(pageSize);
    });

    // The Preferred Action filter needs its options before the first query; wait a render so the
    // filter has them before its default (all checked) is set.
    effect(() => {
      if (this.store.preferredActionOptions()) {
        afterNextRender(() => this.filtersReadyCheck(), { injector: this.injector });
      }
    });
  }

  ngAfterViewInit(): void {
    this.agGrid?.api?.getStatusPanel<IAgExportButton>('export-btn')?.updateExportActive(true);
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  //#region Filters

  filtersReadyCheck() {
    // Can run after a render once the page has been left.
    if (this.agGrid?.api?.isDestroyed()) {
      return;
    }

    if (!this.isPayerReady || !this.store.preferredActionOptions() || !this.isGridReady() || this.filtersReady) {
      return;
    }

    this.setFiltersByPreset();
    this.filtersReady = true;
    this.loadData();
  }

  // Defaults: Active payers with a risk adjustment policy that aren't omitted.
  setFiltersByPreset() {
    this.form.reset();

    this.payerComp.setSelectedValues([]);
    this.statusComp.setValues([false]);
    this.riskPolicyComp.setValues([true]);
    this.omitPayerComp.setValues([false]);
    this.paSelectComp.setValues([]);
    this.serviceYearsComp.reset();
  }

  // Service Year From / To as query values; '' = Any.
  serviceYearParams(): { start: string; end: string } {
    const { from, to } = this.serviceYearsComp.value();
    return { start: from ? `${from}` : '', end: to ? `${to}` : '' };
  }

  onFilterChange() {
    if (!this.filtersReady) {
      return;
    }

    this.filterChanged$.next();
  }

  onResetClick() {
    this.setFiltersByPreset();
    this.onFilterChange();
  }

  loadData() {
    if (!this.filtersReady || this.agGrid?.api?.isDestroyed()) {
      return;
    }

    const serviceYears = this.serviceYearParams();
    const filters: Omit<PayerConfigQuery, 'Metadata'> = {
      PayerIDs: StringFormatter.FilterObjectToString('ID', this.payerComp.getValues()),
      ConfigStatuses: this.booleanFilterValue(this.statusComp),
      RiskAdjustmentPolicy: this.booleanFilterValue(this.riskPolicyComp),
      OmitPayer: this.booleanFilterValue(this.omitPayerComp),
      // '0' is the API's "all preferred actions" value.
      PreferredActionIDs: this.paSelectComp.allSelected() ? '0' : this.paSelectComp.values().join(','),
      PolicyServiceYearStart: serviceYears.start,
      PolicyServiceYearEnd: serviceYears.end,
    };

    this.store.loadPayerConfigs(filters);
  }

  // '' (no filter) when both options are checked, otherwise '1' / '0'.
  booleanFilterValue(comp: FilterMultiSelectComponent<boolean>): string {
    if (comp.allSelected()) {
      return '';
    }

    return comp.values()[0] ? '1' : '0';
  }

  //#endregion

  //#region AG Grid

  onGridReady() {
    this.isGridReady.set(true);
    this.agGrid?.api?.sizeColumnsToFit();
    this.filtersReadyCheck();
  }

  // New rowData from the store has been applied.
  onRowDataUpdated() {
    const api = this.agGrid?.api;
    if (!api || api.isDestroyed()) {
      return;
    }

    api.getStatusPanel<IAgExportButton>('export-btn')?.updateExportDisabled(!this.store.groups()?.length);

    if (this.expandPayerId != null) {
      api.getRowNode(`${this.expandPayerId}`)?.setExpanded(true);
      this.expandPayerId = null;
    }
  }

  // Clicking anywhere on a payer row (other than its buttons or chevron) toggles its nested grid.
  onRowClicked(event: RowClickedEvent<PayerConfigGroup>) {
    const target = event.event?.target as HTMLElement | undefined;
    if (!event.node.master || target?.closest('button, a, .ag-group-contracted, .ag-group-expanded')) {
      return;
    }

    event.node.setExpanded(!event.node.expanded);
  }

  onExpandAllClick() {
    this.setAllExpanded(true);
  }

  onCollapseAllClick() {
    this.setAllExpanded(false);
  }

  private setAllExpanded(expanded: boolean) {
    this.agGrid?.api?.forEachNodeAfterFilter(node => {
      if (node.master) {
        node.setExpanded(expanded);
      }
    });
  }

  // Shared by the payer grid and the nested policy grids.
  getContextMenuItems = (params: GetContextMenuItemsParams) => {
    if (!params.column || params.column.getColId() == 'actions') {
      return [];
    }

    return [
      {
        name: 'Copy',
        action: () => this.clipboard.copy(this.getCellText(params.api, params.node, params.column)),
        icon: '<span class="ag-icon ag-icon-copy"></span>'
      }
    ];
  };

  onCellKeyDown(event: CellKeyDownEvent | FullWidthCellKeyDownEvent) {
    const kbEvent = event.event as KeyboardEvent;
    if (!('column' in event) || !kbEvent?.ctrlKey || kbEvent.key !== 'c' || event.column.getColId() == 'actions') {
      return;
    }

    this.clipboard.copy(this.getCellText(event.api, event.node, event.column));
  }

  private getCellText(api: GetContextMenuItemsParams['api'], node: IRowNode | null, column: any): string {
    if (!node) {
      return '';
    }

    const value = api.getCellValue({ rowNode: node, colKey: column, useFormatter: true });
    return value == null ? '' : `${value}`;
  }

  // Called by AgPaginatorComponent through the grid context.
  onPaginatorPageSizeChange(pageSize: number) {
    this.store.savePageSize(pageSize);
  }

  //#endregion

  //#region Actions

  onAddPayerClick() {
    this.openPayerDialog({ actionType: 'add', payerItem: null });
  }

  onEditPayerClick(group: PayerConfigGroup) {
    this.openPayerDialog({ actionType: 'edit', payerItem: group.Item });
  }

  private openPayerDialog(data: any) {
    const dialogRef = this.dialog.open(AddUpdatePayerDialogComponent, new AddUpdatePayerDialogModel(data));

    this.subs.push(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.reload();
        // A new or renamed payer has to show up in the Payer filter too.
        this.payerComp.refreshOptions();
      }
    }));
  }

  // Edit or copy a service year policy. The payer's full policy list is fetched first: it carries the
  // fields the dialog needs (incoming file types) and is used to block overlapping service years.
  onPolicyDialogClick(mode: PolicyDialogMode, row: PayerConfigItem) {
    const group = this.store.groups()?.find(g => g.PayerID == row.PayerID);
    if (!group) {
      return;
    }

    this.subs.push(this.store.getPayerPolicies(row.PayerID).subscribe(async policies => {
      const policy = policies.find(p => p.PolicyID == row.RiskAdjustmentPolicy.PolicyID) ?? row.RiskAdjustmentPolicy;

      await this.payerEditStore.setCurrentPayerItem(group.Item);

      const dialogRef = this.dialog.open(AddUpdateRiskPolicyDialogComponent,
        new AddUpdateRiskPolicyDialogModel({ mode, item: policy, existingPolicies: policies }));

      this.subs.push(dialogRef.afterClosed().subscribe(result => {
        this.payerEditStore.clearState();

        if (result) {
          this.expandPayerId = group.PayerID;
          this.store.reload();
        }
      }));
    }));
  }

  // Called by AgExportButtonComponent through the grid context.
  onExportClick() {
    const filterValues = new PayerConfigFilterValues();
    filterValues.Payer = StringFormatter.FilterObjectToString('Name', this.payerComp.getValues());
    filterValues.Status = this.statusComp.selectedLabel();
    filterValues.RiskAdjustmentPolicy = this.riskPolicyComp.selectedLabel();
    filterValues.OmitPayer = this.omitPayerComp.selectedLabel();
    filterValues.PreferredActionSubmissionType = this.paSelectComp.selectedLabel();
    const serviceYears = this.serviceYearParams();
    filterValues.PolicyServiceYearStart = serviceYears.start;
    filterValues.PolicyServiceYearEnd = serviceYears.end;

    this.store.exportPayerConfigs(filterValues);
  }

  //#endregion

  //#region Formatters

  // "2025 – 2026" from the earliest start year to the latest end year.
  serviceYearRange(group?: PayerConfigGroup): string {
    const years = (group?.Rows ?? []).flatMap(r => [
      PayerPolicyFunctions.toDate(r.RiskAdjustmentPolicy.ServiceYearStart)?.getFullYear(),
      PayerPolicyFunctions.toDate(r.RiskAdjustmentPolicy.ServiceYearEnd)?.getFullYear()
    ]).filter((y): y is number => y != null);

    if (years.length == 0) {
      return '----';
    }

    const low = Math.min(...years);
    const high = Math.max(...years);
    return low === high ? `${low}` : `${low} – ${high}`;
  }

  statusValue(params: ValueFormatterParams) {
    return params.value ? 'Inactive' : 'Active';
  }

  booleanValue(params: ValueFormatterParams) {
    return params.value ? 'Yes' : 'No';
  }

  manualOmitValue(params: ValueFormatterParams) {
    if (params.data?.RiskAdjustmentPolicy?.HasRiskAdjustment == false) {
      return '----';
    }

    return params.value ? 'Yes' : 'No';
  }

  paFilterOptionValue(params: ValueFormatterParams) {
    const policy = params.data?.RiskAdjustmentPolicy;
    if (!params.value || policy?.HasRiskAdjustment == false || policy?.HasManualOmit == true) {
      return '----';
    }

    return params.value.Description;
  }

  // Shows a short code (submission type, abbreviation) as a chip; "not applicable" stays plain text.
  private codeChip(text?: string | null, className = 'ag-code-chip'): HTMLElement | string {
    if (!text || text === '----') {
      return text ?? '';
    }

    const chip = document.createElement('span');
    chip.className = className;
    chip.textContent = text;
    return chip;
  }

  //#endregion
}
